/**
 * Статус сезона — ВЫЧИСЛЯЕМЫЙ (канон терминологии сезона v3,
 * `docs/superpowers/specs/2026-08-07-season-terminology-v3.md` §5).
 *
 * Четыре значения на языке агронома:
 *   · «Не запланирован» — бюджет сезона не утверждён;
 *   · «Запланирован»    — бюджет сезона утверждён;
 *   · «Идёт»            — сегодня внутри дат рамки сезона;
 *   · «Завершён»        — даты рамки прошли.
 *
 * Почему вычисляем, а не читаем колонку: хранимая `seasons.status` мертва
 * (ADR-060 §S8 — сносится отдельной миграцией), её никто не двигал, и прошлые
 * сезоны 2018-2025 годами висели «планируется». Интерфейс врал: сезон прошёл,
 * а система утверждала, что его ещё планируют.
 *
 * ВАЖНО — статус СЕЗОНА ≠ статус БЮДЖЕТА. Бюджет (`season_plans`: планируется /
 * запланирован / пересмотрен / завершён / архив) остаётся своей лестницей и
 * своей колонкой (канон v3 §6). Здесь он участвует только как один вход:
 * «утверждён ли бюджет» для будущего сезона.
 *
 * Приоритет источников (сверху вниз, первый сработавший выигрывает):
 *   1. даты рамки прошли        → «Завершён»  (прошлое сильнее любого плана);
 *   2. сезон помечен текущим    → «Идёт»      (`is_current` считает канон
 *      `current_season_for()` по правилам сезона — он авторитетнее дат, когда
 *      даты не пришли в ответе API);
 *   3. конца рамки нет, а ГОД сезона прошлый → «Завершён» (страховка, см. ниже);
 *   4. сегодня внутри дат рамки → «Идёт»;
 *   5. бюджет утверждён         → «Запланирован»;
 *   6. бюджет НЕ утверждён      → «Не запланирован»;
 *   7. про бюджет ничего не известно (API не отдал) → `null` — вызывающий
 *      НЕ рисует бейдж. Молчание честнее выдуманного «Не запланирован».
 *
 * Пункт 3 — про сезон без дат рамки. `trg_seasons_canon_bounds` заполняет
 * рамку на INSERT, так что в норме таких строк нет, но легаси-строка без
 * `ends_on` иначе улетала бы мимо «Завершённых»: сезон 2019 без дат считался
 * бы по бюджету и вставал в «Плановый» — ровно та жалоба founder'а, которую
 * этот вычислитель и чинит. Год — не догадка: он есть у каждого сезона и
 * прошлый год прошёл независимо от того, какую рамку ему нарисовали.
 * `is_current` выше по приоритету намеренно: если канон явно назвал сезон
 * текущим, спорить с ним арифметикой мы не будем.
 */

export type SeasonComputedStatus = 'not_planned' | 'planned' | 'active' | 'completed';

/** Подписи — ровно слова канона v3, без синонимов («Завершён», не «закрыт»). */
export const SEASON_STATUS_TITLES: Record<SeasonComputedStatus, string> = {
  not_planned: 'Не запланирован',
  planned: 'Запланирован',
  active: 'Идёт',
  completed: 'Завершён',
};

/** Вариант `<Badge>` под статус — только semantic-варианты DS (Earth Tech). */
export const SEASON_STATUS_BADGE: Record<
  SeasonComputedStatus,
  'default' | 'primary' | 'accent-brand' | 'outline'
> = {
  not_planned: 'outline',
  planned: 'primary',
  active: 'accent-brand',
  completed: 'default',
};

export interface SeasonStatusInput {
  /** Начало рамки сезона, ISO `YYYY-MM-DD`. */
  startsOn?: string | null;
  /** Конец рамки сезона, ISO `YYYY-MM-DD`. */
  endsOn?: string | null;
  /**
   * Год сезона. Страховка на случай, когда конца рамки нет: прошлый год —
   * это прошлое, каким бы ни был бюджет (см. п. 3 в шапке модуля).
   */
  year?: number | null;
  /** Сезон, который канон считает текущим (`current_season_for`). */
  isCurrent?: boolean | null;
  /**
   * Утверждён ли бюджет сезона. `null`/`undefined` = неизвестно (в ответе API
   * этих данных нет) — тогда для будущего сезона статус не выводится.
   */
  budgetApproved?: boolean | null;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Локальная (не UTC) дата «сегодня» в ISO `YYYY-MM-DD`. */
export function toIsoDate(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * `today` принимается строкой или `Date` — тесты фиксируют день, продакшн
 * берёт системную дату. Сравнение строковое по ISO: `YYYY-MM-DD` лексикографически
 * упорядочен, и никакой таймзоны в сравнение не затягивается (`new Date(iso)`
 * трактует ISO-дату как UTC и в MSK вечером даёт минус сутки).
 */
function normalizeToday(today?: Date | string): string {
  if (typeof today === 'string' && ISO_DATE_RE.test(today)) return today;
  if (today instanceof Date && !Number.isNaN(today.getTime())) return toIsoDate(today);
  return toIsoDate();
}

/** ISO-дата или null, если пришёл мусор/пусто (в т.ч. `YYYY-MM-DDTHH:mm:ss`). */
function isoDay(value?: string | null): string | null {
  if (!value) return null;
  const head = value.slice(0, 10);
  return ISO_DATE_RE.test(head) ? head : null;
}

/**
 * Статус сезона по канону v3. `null` — данных не хватает, бейдж не рисуем.
 *
 * Граничные дни включаются в сезон: `today === startsOn` и `today === endsOn`
 * — это ещё «Идёт», «Завершён» наступает со следующего дня после `endsOn`.
 */
export function computeSeasonStatus(
  input: SeasonStatusInput,
  today?: Date | string,
): SeasonComputedStatus | null {
  const now = normalizeToday(today);
  const starts = isoDay(input.startsOn);
  const ends = isoDay(input.endsOn);

  if (ends && now > ends) return 'completed';
  if (input.isCurrent) return 'active';
  // Конца рамки нет — судим по году. Иначе сезон 2019 с одной лишь датой
  // начала бесконечно «Идёт», а совсем без дат уезжает в «Плановый».
  if (!ends && input.year != null && input.year < Number(now.slice(0, 4))) return 'completed';
  if (starts && now >= starts && (!ends || now <= ends)) return 'active';

  if (input.budgetApproved === true) return 'planned';
  if (input.budgetApproved === false) return 'not_planned';
  return null;
}

/** Прошёл ли сезон. Используется и для показа посевов завершённых сезонов. */
export function isSeasonCompleted(
  input: SeasonStatusInput,
  today?: Date | string,
): boolean {
  return computeSeasonStatus({ ...input, budgetApproved: null }, today) === 'completed';
}

/** Подпись статуса или `null`, если статус не выводится. */
export function seasonStatusTitle(
  input: SeasonStatusInput,
  today?: Date | string,
): string | null {
  const s = computeSeasonStatus(input, today);
  return s ? SEASON_STATUS_TITLES[s] : null;
}

/**
 * Справочник «сезон такого-то года завершён?» по списку сезонов компании.
 * Нужен там, где строка знает только год (история культур на поле), а даты
 * рамки лежат в списке сезонов.
 *
 * Год, которого НЕТ в списке сезонов, → `false`: лучше не трогать показ, чем
 * объявить сезон завершённым наугад. А вот сезон, который в списке есть, но
 * без дат рамки, теперь честно решается по своему году (п. 3 в шапке модуля).
 */
export function makeSeasonCompletionLookup(
  seasons: { year: number; startsOn?: string | null; endsOn?: string | null }[],
  today?: Date | string,
): (year: number | null | undefined) => boolean {
  const done = new Set<number>();
  for (const s of seasons) {
    if (isSeasonCompleted({ startsOn: s.startsOn, endsOn: s.endsOn, year: s.year }, today)) {
      done.add(s.year);
    }
  }
  return (year) => (year == null ? false : done.has(year));
}
